import { BrowserRouter, HashRouter, MemoryRouter, Navigate, Route, Routes } from 'react-router-dom';
import React, { useMemo } from 'react';
import { getPagePathname } from './getPagePathname';
import { useHandleGoToPageEvent } from './hooks/useHandleGoToPageEvent';

const supportedRouters = {
  memory: MemoryRouter,
  browser: BrowserRouter,
  hash: HashRouter,
};

type Args = {
  pageId: string | undefined;
  pageInputs?: Record<string, unknown>;
  children: React.ReactNode;
  routerType: keyof typeof supportedRouters;
  basePath?: string;
};

const Router = ({
  pageId,
  pageInputs,
  children,
}: Pick<Args, 'pageId' | 'pageInputs' | 'children'>) => {
  const pagePathname = useMemo(() => getPagePathname({ pageId, pageInputs }), [pageId, pageInputs]);

  useHandleGoToPageEvent({ pageId, pageInputs });

  return (
    <Routes>
      <Route path="/:pageSlug" element={children} />
      <Route
        path="*"
        // When app is mounted, pageSlug won't be defined, and we will always come here in this fallback and navigate to correct path
        element={pagePathname ? <Navigate to={pagePathname} replace /> : children}
      />
    </Routes>
  );
};

function RouterContainer({ routerType = 'memory', basePath, pageId, pageInputs, children }: Args) {
  const RouterComp = supportedRouters[routerType];

  return (
    <RouterComp basename={basePath}>
      <Router pageId={pageId} pageInputs={pageInputs}>
        {children}
      </Router>
    </RouterComp>
  );
}

export default RouterContainer;
